import './style.scss'
import { Button, Checkbox, Input, Select, Space } from 'antd'
import axios from 'axios'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as homeApi from '@/api/home'
import * as Api from '@/api/login'
import { UserStore } from '@/global-states'
import { eventTracking, showCouponModal } from '@/libs/util'
import { MobileOutlined, RedoOutlined, WechatOutlined } from '@ant-design/icons'
import { LoginStore } from '../../states'

interface IProps {
  onLoginSuccess?: (url?: string) => void
}

const MobileLogin: FC<IProps> = (props) => {
  const { t } = useTranslation(['login'])
  const { phone, agreementChecked, loginType } = LoginStore
  const { onLoginSuccess } = props
  const [mobileError, setMobileError] = useState(false)
  const [captcha, setCaptcha] = useState('')
  const [captchaError, setCaptchaError] = useState(false)
  const [captchaButtonDisableMessage, setCaptchaButtonDisableMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const picCaptchaRef = useRef<any>()
  const env = import.meta.env
  const [ticket, setTicket] = useState<any>()
  const [wxLoginStatus, setWxLoginStatus] = useState<any>()
  const scene = useRef<any>()
  const [countryCode, setCountryCode] = useState('86')

  let captchaCode: any
  const statusRef = useRef<any>()
  const phoneLimitLength = useMemo(() => {
    ;(window as any).countryCode = countryCode
    if (countryCode == '852' || countryCode == '853') {
      return 8
    }
    if (countryCode == '886') {
      return 9
    }
    if (countryCode == '86') {
      return 11
    }
  }, [countryCode, phone])

  useEffect(() => {
    LoginStore.loginType = 1
    initCaptcha()
  }, [])

  useEffect(() => {
    if (phoneLimitLength) {
      setMobileError(phone.length > phoneLimitLength)
    } else {
      setMobileError(false)
    }
  }, [phone, phoneLimitLength])

  useEffect(() => {
    setCaptchaError(captcha.length > 6)
  }, [captcha])

  const initCaptcha = () => {
    ;(window as any).initAliyunCaptcha({
      SceneId: '4cgjkj1e',
      prefix: '8imnku',
      mode: 'popup',
      element: '#captcha-element',
      button: '#captcha-button',
      captchaVerifyCallback: captchaVerifyCallback,
      onBizResultCallback: onBizResultCallback,
      getInstance: getInstance,
      slideStyle: {
        width: 360,
        height: 40
      },
      language: 'cn',
      region: 'cn'
    })
  }

  const captchaVerifyCallback = async (captchaVerifyParam: any) => {
    const res: any = await axios.post(`${env.VITE_API_BASE_URL_AUTH}api/v1/meta/check_captcha`, {
      params: captchaVerifyParam,
      phone: (window as any).phone
    })

    const code = res?.data?.code
    if (code) {
      getCaptcha(code)
    }

    const verifyResult = {
      captchaResult: !!code,
      bizResult: !!code
    }
    return verifyResult
  }

  const onBizResultCallback = (bizResult: any) => {
    if (bizResult === true) {
      console.log(t('login.biz_success'))
    } else {
      console.log(t('login.biz_fail'))
    }
  }

  const getInstance = (instance: any) => {
    captchaCode = instance
  }

  const onMobileChange = (e: any) => {
    ;(window as any).phone = e.target.value.replaceAll(/\D/g, '')
    LoginStore.phone = e.target.value.replaceAll(/\D/g, '')
  }

  const oncCaptchaChange = (e: any) => {
    setCaptcha(e.target.value.replaceAll(/\D/g, ''))
  }

  const onAgreeChange = (e: any) => {
    LoginStore.agreementChecked = e.target.checked
  }

  const getCaptcha = async (check_code?: string) => {
    eventTracking('SendVerificatioCode', {
      phone: (window as any).phone || phone
    })
    const country_code = (window as any).countryCode || countryCode
    const res = await Api.sendSms({
      phone: (window as any).phone || phone,
      code_type: 'REORLOGINCODE',
      check_code,
      country_code: country_code === '2' ? '1' : country_code
    })
    if (res.code === 201) {
      picCaptchaRef.current?.click()
    } else {
      let index = 60
      setCaptchaButtonDisableMessage(t('login.captcha_sent', { count: index }))
      const timer = setInterval(() => {
        index--
        setCaptchaButtonDisableMessage(t('login.captcha_sent', { count: index }))
        if (index < 1) {
          clearInterval(timer)
          setCaptchaButtonDisableMessage('')
        }
      }, 1000)
    }
  }

  const onPressEnter = () => {
    if (phone.length === phoneLimitLength && captcha.length === 6 && agreementChecked) {
      doLogin()
    }
  }

  const doLogin = async (wxLogin?: boolean) => {
    setLoading(true)
    const invite_code = sessionStorage.getItem('code')
    const bd_vid = sessionStorage.getItem('bd_vid')
    const msclkid = sessionStorage.getItem('msclkid')
    const promoter_code = localStorage.getItem('promoterCode')
    const utm = sessionStorage.getItem('utm')
    const ad_vid = bd_vid || msclkid
    let ad_type = ''
    let user

    if (bd_vid) {
      ad_type = 'BAIDU_SEM'
    } else if (msclkid) {
      ad_type = 'BING_SEM'
    }

    try {
      if (wxLogin) {
        user = await Api.login({
          type: 'WECHAT_QRCODE',
          invite_code,
          scene: scene.current,
          ad_vid,
          ad_type,
          utm
        })
      } else {
        user = await Api.login({
          phone,
          code: captcha,
          type: 'CODE',
          invite_code,
          country_code: countryCode === '2' ? '1' : countryCode,
          ad_vid,
          ad_type,
          scene: scene.current,
          promoter_code,
          utm
        })
      }

      eventTracking('user_login')
      if (!user?.token) {
        throw new Error(t('login.login_fail'))
      }
      UserStore.setUserToken(user)
      UserStore.checkUserPackage()
      UserStore.getQaCompleted()
      UserStore.loginStatus = UserStore.loginStatus + 1
      const redirectUrl = sessionStorage.getItem('redirectUrl')
      checkGuideRead()
      if (invite_code && redirectUrl) {
        sessionStorage.removeItem('redirectUrl')
        setTimeout(() => {
          onLoginSuccess?.(`${redirectUrl}?code=${invite_code}`)
        })
      } else {
        onLoginSuccess?.(`/`)
      }
    } finally {
      setLoading(false)
    }
  }

  const checkGuideRead = async () => {
    const result = await homeApi.getCreations({
      page: 0,
      size: 1,
      di: UserStore.device_id
    })

    if (!result?.list?.length && !localStorage.getItem('systemGuideRead')) {
      receviceCoupon()
      eventTracking('SystemGuideNextStep0')
      if (location.href.includes('create-video')) {
        UserStore.systemGuideStep = 2
      } else {
        UserStore.systemGuideStep = 1
      }
    } else {
      receviceCoupon(true)
    }
    setTimeout(() => {
      UserStore.systemReady = true
    }, 100)
  }

  const receviceCoupon = async (open?: boolean) => {
    const res = await homeApi.getUnclaimedCoupons()
    const coupon = res?.list?.find((l: any) => l.name === 'first_membership_order' && l.status === 1)
    if (coupon) {
      await homeApi.receviceCoupon(coupon.id)
      !!open && showCouponModal()
      UserStore.couponTip = UserStore.couponTip + 1
    }
  }

  const onLoginByWx = async () => {
    const res = await Api.getWxLoginQrcode()
    LoginStore.loginType = 2
    scene.current = res.scene
    setTicket(res)
    clearEvent()
    setWxLoginStatus(undefined)
    statusRef.current = setInterval(() => {
      checkWxLoginStatus()
    }, 1000)
    setCaptcha('')
    LoginStore.phone = ''
    LoginStore.agreementChecked = true
  }

  const onMobileLogin = () => {
    LoginStore.loginType = 1
    setWxLoginStatus(undefined)
    setTicket(undefined)
    scene.current = undefined
    clearEvent()
  }

  const checkWxLoginStatus = async () => {
    try {
      const res = await Api.checkWxLoginStatus({
        scene: scene.current
      })

      if (res?.status) {
        clearEvent()
      }

      if (res?.status === 2) {
        doLogin(true)
      } else {
        setWxLoginStatus(res?.status)
      }
    } catch {
      clearEvent()
    }
  }

  const clearEvent = () => {
    if (statusRef.current) {
      clearInterval(statusRef.current)
    }
  }

  return (
    <div className="mobile-login">
      {(loginType === 1 || wxLoginStatus === 1) && (
        <>
          {wxLoginStatus === 1 && (
            <>
              <div className="bind-title">{t('login.bind_title')}</div>
              <div className="bind-desc">{t('login.bind_desc')}</div>
            </>
          )}

          <div className="login-phone">
            <Select
              popupClassName="coutry-select"
              value={countryCode}
              onChange={(v) => setCountryCode(v)}
              options={[
                { label: '+86', value: '86', desc: t('login.country_china') },
                { label: '+886', value: '886', desc: t('login.country_taiwan') },
                { label: '+852', value: '852', desc: t('login.country_hongkong') },
                { label: '+853', value: '853', desc: t('login.country_macau') },
                { label: '+1', value: '1', desc: t('login.country_usa') },
                { label: '+1', value: '2', desc: t('login.country_canada') },
                { label: '+44', value: '44', desc: t('login.country_uk') },
                { label: '+61', value: '61', desc: t('login.country_australia') },
                { label: '+81', value: '81', desc: t('login.country_japan') },
                { label: '+82', value: '82', desc: t('login.country_korea') },
                { label: '+65', value: '65', desc: t('login.country_singapore') },
                { label: '+60', value: '60', desc: t('login.country_malaysia') },
                { label: '+63', value: '63', desc: t('login.country_philippines') },
                { label: '+49', value: '49', desc: t('login.country_germany') },
                { label: '+33', value: '33', desc: t('login.country_france') },
                { label: '+39', value: '39', desc: t('login.country_italy') },
                { label: '+34', value: '34', desc: t('login.country_spain') },
                { label: '+7', value: '7', desc: t('login.country_russia') },
                { label: '+55', value: '55', desc: t('login.country_brazil') },
                { label: '+52', value: '52', desc: t('login.country_mexico') },
                { label: '+90', value: '90', desc: t('login.country_turkey') },
                { label: '+966', value: '966', desc: t('login.country_saudi') },
                { label: '+54', value: '54', desc: t('login.country_argentina') },
                { label: '+98', value: '98', desc: t('login.country_iran') },
                { label: '+66', value: '66', desc: t('login.country_thailand') },
                { label: '+84', value: '84', desc: t('login.country_vietnam') },
                { label: '+91', value: '91', desc: t('login.country_india') },
                { label: '+62', value: '62', desc: t('login.country_indonesia') },
                { label: '+855', value: '855', desc: t('login.country_cambodia') },
                { label: '+856', value: '856', desc: t('login.country_laos') },
                { label: '+95', value: '95', desc: t('login.country_myanmar') },
                { label: '+673', value: '673', desc: t('login.country_brunei') },
                { label: '+27', value: '27', desc: t('login.country_south_africa') },
                { label: '+971', value: '971', desc: t('login.country_uae') },
                { label: '+31', value: '31', desc: t('login.country_netherlands') },
                { label: '+41', value: '41', desc: t('login.country_switzerland') },
                { label: '+46', value: '46', desc: t('login.country_sweden') },
                { label: '+47', value: '47', desc: t('login.country_norway') },
                { label: '+358', value: '358', desc: t('login.country_finland') },
                { label: '+45', value: '45', desc: t('login.country_denmark') },
                { label: '+30', value: '30', desc: t('login.country_greece') },
                { label: '+48', value: '48', desc: t('login.country_poland') },
                { label: '+32', value: '32', desc: t('login.country_belgium') },
                { label: '+43', value: '43', desc: t('login.country_austria') },
                { label: '+353', value: '353', desc: t('login.country_ireland') },
                { label: '+351', value: '351', desc: t('login.country_portugal') },
                { label: '+972', value: '972', desc: t('login.country_israel') },
                { label: '+92', value: '92', desc: t('login.country_pakistan') },
                { label: '+880', value: '880', desc: t('login.country_bangladesh') },
                { label: '+234', value: '234', desc: t('login.country_nigeria') },
                { label: '+254', value: '254', desc: t('login.country_kenya') },
                { label: '+20', value: '20', desc: t('login.country_egypt') },
                { label: '+212', value: '212', desc: t('login.country_morocco') },
                { label: '+213', value: '213', desc: t('login.country_algeria') }
              ]}
              optionRender={(option) => (
                <Space>
                  <label style={{ display: 'inline-block', width: '80px', cursor: 'pointer' }}>
                    {option.data.label}
                  </label>
                  {option.data.desc}
                </Space>
              )}
            ></Select>
            <Input
              className="input-with-hint"
              value={phone}
              maxLength={11}
              onChange={onMobileChange}
              placeholder={
                phoneLimitLength
                  ? t('login.phone_placeholder', { count: phoneLimitLength })
                  : t('login.phone_placeholder_default')
              }
              status={mobileError ? 'error' : undefined}
            />
          </div>
          <div className="login-capt">
            <Input
              className="input-with-hint"
              value={captcha}
              maxLength={6}
              onChange={oncCaptchaChange}
              status={captchaError ? 'error' : undefined}
              placeholder={t('login.captcha_placeholder')}
              onPressEnter={onPressEnter}
            />
            <Button
              className="borderless btn-capt black"
              onClick={() => getCaptcha()}
              type="primary"
              disabled={!!captchaButtonDisableMessage || (phoneLimitLength && phone.length !== phoneLimitLength)}
            >
              {captchaButtonDisableMessage || t('login.get_captcha')}
            </Button>
          </div>

          <Button
            className="mobile-login-button"
            type="primary"
            disabled={
              (phoneLimitLength && phone.length !== phoneLimitLength) || captcha.length !== 6 || !agreementChecked
            }
            onClick={() => doLogin()}
            loading={loading}
          >
            {wxLoginStatus === 1 ? t('login.confirm_bind') : t('login.login')}
          </Button>

          {wxLoginStatus === 1 && (
            <div className="login-back gradient" onClick={onMobileLogin}>
              {t('login.back_login')}
            </div>
          )}

          {!wxLoginStatus && (
            <div className="login-agree">
              <Checkbox checked={agreementChecked} onChange={onAgreeChange}>
                <label>{t('login.agree_prefix')}</label>
                <a className="gradient" href="/eula.html" target="_blank">
                  {t('login.user_agreement')}
                </a>
                <label>{t('login.agree_and')}</label>
                <a className="gradient" href="/privacy_agreement.html" target="_blank">
                  {t('login.privacy_agreement')}
                </a>
              </Checkbox>
            </div>
          )}
        </>
      )}

      {(!wxLoginStatus || wxLoginStatus === 3) && (
        <div className="wechat-login">
          <div className={`wechat-title ${loginType === 2 ? '' : 'title-border'}`} onClick={onLoginByWx}>
            <WechatOutlined />
            <span>{t('login.wechat_login')}</span>
          </div>
          {loginType === 2 && (
            <>
              <div className="wechat-desc">{t('login.wechat_desc')}</div>
              <div className="wechat-code">
                <img src={`https://mp.weixin.qq.com/cgi-bin/showqrcode?ticket=${ticket?.ticket}`} />

                {wxLoginStatus === 3 && (
                  <div className="mask" onClick={onLoginByWx}>
                    <RedoOutlined />
                  </div>
                )}
              </div>
              <div className="wechat-agree">
                <span>{t('login.login_agree_prefix')}</span>
                <a href="/eula.html" target="_blank">
                  {t('login.user_agreement')}
                </a>
                <label>{t('login.agree_and')}</label>
                <a href="/privacy_agreement.html" target="_blank">
                  {t('login.privacy_agreement')}
                </a>
              </div>
              <div className="wechat-switch" onClick={onMobileLogin}>
                <MobileOutlined />
                <span>{t('login.switch_mobile')}</span>
              </div>
            </>
          )}
        </div>
      )}

      <Button ref={picCaptchaRef} id="captcha-button" style={{ display: 'none' }}>
        {t('login.captcha_button')}
      </Button>

      <div id="captcha-element"></div>
    </div>
  )
}

export default MobileLogin
